import { Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TemplateService } from '../../services/template.service';
import { ToastService } from '../../../../core/services/toast.service';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { LogActionDTO, LoginLogsService } from '../../../../shared/components/layout/dashboard/loginLogs/login-logs.service';

@Component({
  selector: 'app-template-form',
  templateUrl: './template-form.component.html',
  styleUrls: ['./template-form.component.scss']
})
export class TemplateFormComponent implements OnInit {
    isSubmitting = false;
  private directCodeInputTimeout: any;
  @Output() templateCreated = new EventEmitter<any>();
  private loggedIn = new BehaviorSubject<boolean>(false);
  form!: FormGroup;
  copied = false;
  fields = [
    { name: 'consultantName', placeholder: 'Consultant Name', label: 'Consultant Name', type: 'text', required: true },
    { name: 'presentRole', placeholder: 'Present Role', label: 'Present Role', type: 'text', required: true },
    { name: 'interviewingRole', placeholder: 'Interviewing Role', label: 'Interviewing Role', type: 'text', required: true },
    { name: 'interviewingClient', placeholder: 'Interviewing Client', label: 'Interviewing Client', type: 'text', required: true },
    { name: 'presentClient', placeholder: 'Present Client', label: 'Present Client', type: 'text', required: true },
    { name: 'previousClient', placeholder: 'Previous Client', label: 'Previous Client', type: 'text', required: true },
    {
      name: 'interviewType',
      placeholder: 'Interview Type',
      label: 'Interview Type',
      type: 'select',
      required: true,
      options: ['practice', 'screening', 'firstround', 'finalround']
    },
    {
      name: 'jobDescription',
      placeholder: 'e.g. Deep learning, distributed systems...',
      label: 'Job Description',
      type: 'textarea',
      required: true
    }
  ];
  isDropdownOpen = false;
  prompts: any[] = [];
  displayedPrompts: any[] = [];
  selectedPrompt: any = null;
  loadingPrompts = false;
  directCode: string = '';
  directCodeError: string = '';
  sidebarHidden = true;
  isMobile: boolean = false;
  menuOpen: boolean = false;
  loginLogsService: LoginLogsService;
  constructor(
    private fb: FormBuilder,
    private templateService: TemplateService,
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService,
    loginLogsService: LoginLogsService
  ) { this.loginLogsService = loginLogsService; }

  ngOnInit(): void {
    this.buildForm();
    this.fetchPrompts();
    this.checkScreenSize()
  }

  buildForm(): void {
    const group: { [key: string]: any } = {};
    this.fields.forEach(field => {
      const validators = field.required ? [Validators.required] : [];
      group[field.name] = ['', validators];
    });
    this.form = this.fb.group(group);
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }
  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobile = window.innerWidth <= 1080;
  }
  fetchPrompts(): void {
    this.loadingPrompts = true;
    this.displayedPrompts = [];
    this.templateService.getAllPrompts().subscribe({
      next: (data: any[]) => {
        this.prompts = data;
        this.loadingPrompts = false;
        this.loadPromptsStepByStep();
      },
      error: () => {

        this.loadingPrompts = false;
      }
    });
  }

  loadPromptsStepByStep(): void {
    this.displayedPrompts = [];
    let index = 0;
    const addNextPrompt = () => {
      if (index < this.prompts.length) {
        this.displayedPrompts.push(this.prompts[index]);
        index++;
        setTimeout(addNextPrompt, 50);
      }
    };
    addNextPrompt();
  }

  selectPrompt(prompt: any): void {
    this.selectedPrompt = prompt;
    this.form.patchValue({
      consultantName: prompt.consultantName || '',
      presentRole: prompt.presentRole || '',
      interviewingRole: prompt.interviewingRole || '',
      interviewingClient: prompt.interviewingClient || '',
      presentClient: prompt.presentClient || '',
      previousClient: prompt.previousClient || '',
      interviewType: prompt.interviewType || '',
      jobDescription: prompt.jobDescription || ''
    });
  }

  clearSelection(): void {
    this.selectedPrompt = null;
    this.form.reset();
  }


  reloadChat(): void {
    if (this.selectedPrompt) {
      this.router.navigate(['/chat'], { queryParams: { templateId: this.selectedPrompt.id } });
    }
  }
  toggleSidebar(): void {
    this.sidebarHidden = !this.sidebarHidden
  }
  logout(): void {
    this.loggedIn.next(false); // Broadcast that the user is logged out
    const loginLogId = sessionStorage.getItem('loginLogId');
    if (loginLogId) {
      this.loginLogsService.logUpdate(loginLogId, () => {
        this.authService.signOut();
      });
    } else {
      this.authService.signOut();
      localStorage.removeItem('isLoggedIn');
      this.router.navigate(['/auth/sign-in']);
    }
  }

  onSubmit(): void {
    if (this.form.invalid || this.isSubmitting) return;

    this.isSubmitting = true;
    const formValues = this.form.value;

    let promptTemplate = `
  ROLE

  You are a 10+ years experienced ${formValues.presentRole} interviewing for a ${formValues.interviewingRole} position at a large tech company.
  Interview Type: ${formValues.interviewType}
  Job Description: ${formValues.jobDescription}
  Current Client: ${formValues.presentClient} | Previous Client: ${formValues.previousClient}.
  Developed by Otter AI, a SBC group product

  CRITICAL - FORBIDDEN WORDS (NEVER USE THESE)
  NEVER use these corporate AI buzzwords in ANY response:
  "extensively"
  "leveraging" / "leverage"
  "utilizing" / "utilize"

  These words immediately sound like AI. Real people say:
  "a lot", "pretty much", "regularly", "often"
  "use", "work with"
  "use"

  If you catch yourself about to use these words, STOP and rephrase.

  ANSWERING STYLE
  Be Direct & Natural:
  Jump straight into answers - no "In my experience at..." or "During my tenure..."
  Provide specific technical details from projects you've worked on
  Use present tense: "I work with", "I use", "I build" (past tense only if specifically asked about past)
  Keep answers concise, paragraph format, conversational
  Highlight key words in bold

  **ANSWER LENGTH:**
  - Keep initial answers SHORT - 3-5 sentences focused on the main point
  - Don't cover every possible aspect unprompted
  - Wait for interviewer to ask follow-up questions before going deeper
  - Real interviews are back-and-forth conversations, not comprehensive lectures
  - If asked broad question like "exp on airflow", give 1-2 main points and wait

  Indian English Conversational:
  Start some answers with "Basically" (naturally, not always)
  End statements with "right" for emphasis (e.g., "this works better, right")
  Use fillers naturally: "actually", "you know", "I mean", "so", "pretty much", "could be", "whatever"
  Use pauses when thinking: "Hmm...", "Let me think...", "So..."
  Start examples with: "Like...", "For example...", "Say you have...", "One time we had..."
  Use analogies: "It's kind of like...", "Think of it this way..."
  Break long sentences with dashes: "could be databases, APIs, files - whatever the source is"

  Team & Storytelling:
  Mention team naturally: "We discussed with the team...", "My lead suggested..."
  For behavioral questions: "So what happened was...", "Initially struggled with...", "That taught me..."

  TECHNICAL APPROACH
  Realistic Metrics:
  Transaction volumes: hundreds to low thousands per hour
  TPS: 5–50
  Data processing: 5–30 MB/s
  Use approximations: "around X", "maybe X", not exact numbers

  Focus on Approach, NOT Results:
  Describe what you did and the tech used, explain constraints and limitations
  NEVER mention improvement percentages (Exception: test coverage like "around 80%")
  Instead of "improved by 30%", say "implemented partitioning which helped performance"
  Use vague qualifiers: "made it faster", "helped with the load", "works better now"
  Frame as ongoing: "keep optimizing", "made some improvements", "tuned it to work better"
  Avoid dramatic language: "achieved", "boosted", "dramatically improved"

  Handle False Premises:
  If interviewer states something technically WRONG, immediately correct it politely
  NEVER accept false premise and fabricate answer around it
  Say: "Actually, that's not quite right..." then explain the correct concept
  Example: Interviewer says "When Kafka uses 2PC..." → Correct: "Kafka doesn't use 2PC, it uses transactional API. For cross-service atomicity, we use Saga pattern instead."

  Architecture Diagrams:
  Keep diagrams SIMPLE - don't overcomplicate with too many components
  Focus on main data flow, not every detail
  Use simple ASCII box diagrams with single-line box-drawing characters
  Create boxes using: ┌ for top-left corner, ┐ for top-right corner, └ for bottom-left corner, ┘ for bottom-right corner
  Use ─ for horizontal lines, │ for vertical lines
  Connect boxes with arrows using ─> for horizontal flow, │ with ▼ for vertical flow down, │ with ▲ for vertical flow up
  Use ├ ┤ ┬ ┴ for T-junctions and connectors when needed
  Keep layout clean with proper spacing - show flow left to right or top to bottom
  Brief explanation after diagram focusing on clarity
  Make boxes uniform size where possible, align components properly

  CODING GUIDELINES
  Code Rules:
  Must be complete, executable, error-free in any language (Python/Java/SQL/JavaScript - choose based on context)
  Has main() method for testing
  Include imports/dependencies, sample data if needed - copy-paste ready
  ZERO comments unless truly complex - code should be self-explanatory
  Shortest variable names: 'n', 'res', 'arr' not 'numberOfElements', 'resultList'

  When Modifying Code:
  Use diff-style: -- for DELETE (# DELETE or // DELETE), ++ for ADD (# ADD or // ADD)
  Show complete final code after diff

  Style:
  SHORTEST, most optimized code - remove everything unnecessary
  Fast, clean, reliable solutions - proven patterns
  Minimal comments (only complex logic)
  Vary variable names slightly: 'df'/'data'/'salesData', use realistic file names: 'sales_q3.csv' not 'input.csv'
  Brief explanation after: approach, what changed, complexity if relevant

  CRITICAL CODING RULES
  1. SHORTEST CODE ONLY:
  Write the MOST OPTIMIZED, SHORTEST solution - no fluff
  ZERO unnecessary comments - only comment truly complex logic
  Concise variable names: 'n', 'i', 'res', 'arr' not 'numberOfItems', 'resultArray'
  No javadoc, no explanatory comments for obvious code
  Strip everything that's not essential

  2. **WHEN MODIFYING CODE:**
  Show the COMPLETE code with diff markers inline:
  - Mark new/changed lines with: ++  // ADD THIS LINE
  - Mark deleted lines with: --  // DELETE THIS LINE
  - Keep all existing code without markers

  Example - User asks "add age parameter and print it":

  public class Person {
    private String name;
  --  public void display(String name) {           // DELETE THIS LINE
  +  public void display(String name, int age) {  // ADD THIS LINE
      System.out.println(name);
  +      System.out.println("Age: " + age);       // ADD THIS LINE
    }
  }

  TRIGGER WORDS: "add", "modify", "change", "update", "now add", "make it"
  3. COPY-PASTE READY:
  Must compile and run immediately in online compiler
  Include all imports, include main() method
  Zero errors, zero missing dependencies

  FINAL REMINDER: Never use "extensively", "leveraging", or "utilizing"

  The interviewer will now ask you technical questions. Respond as described above.
  `;

    const id = this.generateUUID();
    const payload = {
      id,
      prompt: promptTemplate,
      name: formValues.consultantName,
      consultantName: formValues.consultantName,
      presentRole: formValues.presentRole,
      interviewingRole: formValues.interviewingRole,
      interviewingClient: formValues.interviewingClient,
      presentClient: formValues.presentClient,
      previousClient: formValues.previousClient,
      interviewType: formValues.interviewType,
      jobDescription: formValues.jobDescription
    };

    this.templateService.createTemplate(payload).subscribe({
      next: (created: { id: string }) => {
        this.isSubmitting = false;
        this.router.navigate(['/templates/share-interview', created.id]);
        this.appendLogActionAsync(id, formValues.consultantName);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.toastService.show('Template creation failed. Please try again.');
        console.error('Template creation failed:', err);
      }
    });
  }

  private appendLogActionAsync(promptId: string, name: string): void {
    const loginLogId = sessionStorage.getItem('loginLogId');
    
    if (!loginLogId || loginLogId.trim().length === 0) {
      console.warn('No loginLogId found in session, skipping log action');
      return;
    }

    const logAction: LogActionDTO = {
      id: this.loginLogsService.generateUUID(),
      promptId: promptId,
      actionName: name || 'Interview Form Created',
      timestamp: Date.now()
    };

    this.loginLogsService.appendLogAction(loginLogId, logAction).subscribe({
      next: (response: any) => {
        console.log('Log action appended successfully:', response);
      },
      error: (err: any) => {
        console.error('Failed to append log action:', err);
      }
    });
  }



  copyInterviewCode(): void {
    const code = this.selectedPrompt?.id || '';
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 1000);
    })
  }

  goToChatWithCode(code: string): void {
    if (!code || !/^[0-9]{6}$/.test(code)) {
      this.directCodeError = 'Interview code must be exactly 6 digits.';
      setTimeout(() => { this.directCodeError = ''; }, 3000);
      return;
    }
    this.directCodeError = '';
    this.templateService.getTemplateById(code).subscribe({
      next: (template) => {
        this.router.navigate(['/chat'], { queryParams: { templateId: code } });
      },
      error: (err) => {
        this.directCodeError = 'Invalid interview code. Please check and try again.';
        this.toastService.show('Invalid interview code. Please check and try again.', 'error', 4000);
        setTimeout(() => {
          this.directCodeError = '';
        }, 3000);
      }
    });

  }

  onDirectCodeInput(): void {
    if (this.directCodeInputTimeout) {
      clearTimeout(this.directCodeInputTimeout);
    }
    this.directCodeError = '';
    this.directCodeInputTimeout = setTimeout(() => {
      if (!/^[0-9]{6}$/.test(this.directCode)) {
        this.directCodeError = 'Interview code must be exactly 6 digits.';
      }
    }, 5000);
  }

  onDirectCodeKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.goToChatWithCode(this.directCode);
    }
  }

  generateUUID(): string {
    let num: string;
    num = Math.floor(100000 + Math.random() * 900000).toString();
    return num;
  }

  navigateToPrompt(promptId: any): void {
    this.router.navigate(['/chat'], { queryParams: { templateId: promptId } });
  }


}
